import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography
} from '@mui/material'
import React, { useState } from 'react'

import CTA from '@f/elements/CTA/CTA'

import { AccordionSection } from '@f-fertighaus/applications/Next/components/fertighaus/AccordionSection/AccordionSection'
import { CakeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/CakeIcon'
import { KfnFundingIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/KfnFundingIcon'
import { LeafBigIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/LeafBigIcon'
import { InfoTooltip } from '@f-fertighaus/applications/Next/components/fertighaus/InfoTooltip/InfoTooltip'
import { DEFAULT_LOCALE } from '@f-fertighaus/applications/Next/config/constants'
import {
  DictionaryKey,
  t
} from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { isNotEmptyArray } from '@f-fertighaus/applications/Next/utils/typeguards/isNotEmptyArray'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { DetailValueWithTooltip } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailElement/DetailElement.styled'
import { DetailsSectionWrapper } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailsSectionWrapper.styled'

import { DetailElement } from './common/DetailElement/DetailElement'
import { DetailList } from './common/DetailList.styled'

const ENERGY_STANDARDS_WITH_DESCRIPTION = [
  'kfw_40_plus',
  'kfw_40',
  'kfw_55'
] as const

export const EnergyStandardAndFunding = () => {
  const [energyAccordionExpanded, setEnergyAccordionExpanded] = useState(false)
  const [fundingAccordionExpanded, setFundingAccordionExpanded] = useState(
    false
  )
  const {
    state: { energy_standard_options, heat_transfer_coefficient, funding }
  } = useHouseDetail()

  const handleEnergyAccordionClick = () => {
    webtrack({
      category: 'accordion',
      action: 'click',
      label: `${
        energyAccordionExpanded ? 'collapse' : 'expand'
      }-engergy-standards`,
      location: 'details-section',
      values: [
        {
          type: 'text',
          value: t(
            `houseDetailPage.sections.details.energy_standards.accordion.headline`
          )
        }
      ]
    })
    setEnergyAccordionExpanded(!energyAccordionExpanded)
  }
  const handleFundingAccordionClick = () => {
    webtrack({
      category: 'accordion',
      action: 'click',
      label: `${fundingAccordionExpanded ? 'collapse' : 'expand'}-kfn-funding`,
      location: 'details-section',
      values: [
        {
          type: 'text',
          value: t(
            `houseDetailPage.sections.details.energy_standards.accordion.headline`
          )
        }
      ]
    })
    setFundingAccordionExpanded(!fundingAccordionExpanded)
  }

  return (
    <DetailsSectionWrapper>
      <Box sx={{ pt: 1, pb: 2 }}>
        <Typography variant='bodyTitle' component='p'>
          {t(`houseDetailPage.sections.details.energy_standards.headline`)}
        </Typography>
      </Box>
      <DetailList>
        <DetailElement
          icon={CakeIcon}
          name={t(
            `houseDetailPage.sections.details.energy_standards.labels.energy_standard`
          )}>
          <DetailValueWithTooltip>
            <div>
              {energy_standard_options?.map((value, index) => (
                <Typography
                  variant='body2'
                  key={value}
                  color={index !== 0 && 'primary.light'}
                  sx={{ marginTop: index === 0 ? '0' : '8px' }}>
                  {t(
                    `houseDetailPage.sections.details.energy_standards.values.${value}` as DictionaryKey
                  )}
                </Typography>
              ))}
            </div>
            {isNotEmptyArray(energy_standard_options) && (
              <InfoTooltip
                title={t(
                  `houseDetailPage.sections.details.energy_standards.tooltips.${energy_standard_options[0]}` as DictionaryKey
                )}
                wtPayload={{
                  location: 'details-section',
                  label: energy_standard_options[0]
                }}
              />
            )}
          </DetailValueWithTooltip>
        </DetailElement>
        {heat_transfer_coefficient && (
          <DetailElement
            icon={LeafBigIcon}
            name={t(
              `houseDetailPage.sections.details.energy_standards.labels.heat_transfer_coefficient`
            )}>
            <DetailValueWithTooltip>
              <Typography variant='body2'>
                {t(
                  `houseDetailPage.sections.details.energy_standards.values.heat_transfer_coefficient`,
                  {
                    number: heat_transfer_coefficient.toLocaleString(
                      DEFAULT_LOCALE
                    )
                  }
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  `houseDetailPage.sections.details.energy_standards.tooltips.heat_transfer_coefficient`
                )}
                wtPayload={{
                  location: 'details-section',
                  label: 'heat_transfer_coefficient'
                }}
              />
            </DetailValueWithTooltip>
          </DetailElement>
        )}
        <Accordion>
          <AccordionSummary onClick={handleEnergyAccordionClick}>
            <Typography variant='body1'>
              {t(
                `houseDetailPage.sections.details.energy_standards.accordion.headline`
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {ENERGY_STANDARDS_WITH_DESCRIPTION.map(value => (
              <AccordionSection
                key={value}
                headline={t(
                  `houseDetailPage.sections.details.energy_standards.accordion.${value}.headline` as DictionaryKey
                )}
                description={t(
                  `houseDetailPage.sections.details.energy_standards.accordion.${value}.description` as DictionaryKey
                )}
              />
            ))}
          </AccordionDetails>
        </Accordion>
        {funding && (
          <Box sx={{ mt: 2 }}>
            <DetailElement
              icon={KfnFundingIcon}
              name={t(
                `houseDetailPage.sections.details.overview.labels.funding`
              )}>
              <DetailValueWithTooltip>
                <Typography variant='body2'>
                  {t(
                    `houseDetailPage.sections.details.overview.values.funding.${funding}`
                  )}
                </Typography>
                <InfoTooltip
                  title={t(
                    `houseDetailPage.sections.details.overview.tooltips.funding.${funding}`
                  )}
                  wtPayload={{
                    location: 'details-section',
                    label: funding
                  }}
                />
              </DetailValueWithTooltip>
            </DetailElement>
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary onClick={handleFundingAccordionClick}>
                <Typography variant='body1'>
                  {t(
                    `houseDetailPage.sections.details.funding.accordion.headline`
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <AccordionSection
                  headline={t(
                    `houseDetailPage.sections.details.funding.accordion.kfn_funding.headline`
                  )}
                  description={t(
                    `houseDetailPage.sections.details.funding.accordion.kfn_funding.description`
                  )}>
                  <Typography variant='body2'>
                    Weitere Informationen finden Sie auf{' '}
                    <CTA
                      tag='a'
                      target='_blank'
                      type='text-primary'
                      href='https://www.kfw.de/inlandsfoerderung/Privatpersonen/Neubau/F%C3%B6rderprodukte/Klimafreundlicher-Neubau-Wohngeb%C3%A4ude-(297-298)/'>
                      der Website der KfW Bank.
                    </CTA>
                  </Typography>
                </AccordionSection>
                <AccordionSection
                  headline={t(
                    `houseDetailPage.sections.details.funding.accordion.kfn_funding_qng.headline`
                  )}
                  description={t(
                    `houseDetailPage.sections.details.funding.accordion.kfn_funding_qng.description`
                  )}>
                  <Typography variant='body2'>
                    Weitere Informationen finden Sie auf{' '}
                    <CTA
                      tag='a'
                      target='_blank'
                      type='text-primary'
                      href='https://www.kfw.de/inlandsfoerderung/Privatpersonen/Neubau/F%C3%B6rderprodukte/Klimafreundlicher-Neubau-Wohngeb%C3%A4ude-(297-298)/'>
                      der Website der KfW Bank.
                    </CTA>
                  </Typography>
                </AccordionSection>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </DetailList>
    </DetailsSectionWrapper>
  )
}
